import axios from "axios";
import { getStoredToken } from "./clientStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000";
export const API_URL = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API_URL,
    timeout: 30000,
    headers: { "Content-Type": "application/json" },
});

// Attach bearer token from localStorage (backend accepts Authorization: Bearer)
api.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
    }
    return config;
});

export function formatApiError(detail, fallback = "Request failed. Please try again.") {
    if (detail == null || detail === "") return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        const msg = detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
        return msg || fallback;
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    if (typeof detail === "object" && typeof detail.message === "string") return detail.message;
    return String(detail);
}

/** Extract a user-visible message from an axios error. */
export function extractAxiosError(error, fallback = "Request failed. Please try again.") {
    if (!error) return fallback;
    if (error.response) {
        const { status, data } = error.response;
        const detail = data?.detail ?? data?.message ?? data?.error;
        const msg = formatApiError(detail, null);
        if (msg) return msg;
        return status ? `Request failed (${status})` : fallback;
    }
    if (error.code === "ECONNABORTED") {
        return `Request timed out reaching ${API_URL}. The server may still be waking up — wait a moment and try again.`;
    }
    if (error.request) {
        return `Cannot reach API at ${API_URL}. Is the backend running?`;
    }
    return error.message || fallback;
}

/** Admin user list — longer timeout + retry for cold starts / heavy first load. */
export async function fetchAdminUsersList() {
    const maxAttempts = 3;
    let lastError;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
            return await api.get("/admin/users", { timeout: 90000 });
        } catch (error) {
            lastError = error;
            const retryable = !error.response && attempt < maxAttempts - 1;
            if (!retryable) break;
            await new Promise((resolve) => setTimeout(resolve, 1500 * (attempt + 1)));
        }
    }
    throw lastError;
}
